import React, { useState, useEffect } from "react";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { makeRequest2 } from "../../../../../../utils/makeRequest";
import { toastify } from "../../../../../../utils/toast";
import {
  ADD_WATER_METER_SETTINGS_URL,
  GET_WATER_METER_SETTINGS_URL,
  UPDATE_WATER_METER_SETTINGS_URL,
  getBillerAddressesURL,
  getBankDetailsURL,
  fetchFacilityPaymentDetails as FETCH_FACILITY_PAYMENT_DETAILS_URL,
} from "../../../../../../utils/urls";
import { getGLAccountsURL, getFacilityPaymentDetailsURL, getUsersURL } from "../../../../../../utils/urls";
import { useFacilityStore } from "../../../../../../app/z-store/store";

const SettingsTab = () => {
  const facilityId = useFacilityStore((state) => state.facilityId);

  const [formData, setFormData] = useState({
    minAmount: 0,
    maxAmount: 10000,
    gracePeriod: 10,
    invoiceDay: 3,
    enforcePayment: "",
    minimumPaymentAmount: 0,
    tariff: "",
    tariffAmount: 0,
    tariffAmountSmart: 0,
    fixedTariffAmount: 0,
    meterLoan: 0,
    freeWaterAllowance: 0,
    notifications: {
      usageAlerts: {
        enabled: false,
        daily: false,
        weekly: false,
        monthly: false
      },
      statements: {
        enabled: false,
        statementOwner: {
          name: '',
          email: '',
          phone: ''
        }
      },
      paymentReminders: {
        enabled: false,
        daysBeforeDue: 3,
        frequency: 'once'
      }
    },
    otherCharges: "",
    sewerageCharge: 0,
    fixedCharge: 0,
    vatPercentage: 0,
    lowThreshold: 0,
    highThreshold: 0,
    billerAddress: {
      name: '',
      email: '',
      phone: '',
      address: '',
      city: ''
    },
    glAccounts: {
      invoice: {
        debit: '',
        credit: ''
      },
      payment: {
        debit: '',
        credit: ''
      }
    },
    discounts: [],
    paymentMethods: {
      mobilePayment: {
        status: false,
        paymentId: ''
      },
      bankPayment: {
        status: false,
        paymentId: ''
      }
    }
  });

  const [glAccounts, setGlAccounts] = useState([]);
  const [loadingGLAccounts, setLoadingGLAccounts] = useState(false);
  const [billerAddresses, setBillerAddresses] = useState([]);
  const [loadingBillerAddresses, setLoadingBillerAddresses] = useState(false);
  const [selectedBillerAddressId, setSelectedBillerAddressId] = useState('');
  const [bankDetails, setBankDetails] = useState([]);
  const [loadingBankDetails, setLoadingBankDetails] = useState(false);
  const [facilityPaymentDetails, setFacilityPaymentDetails] = useState([]);
  const [loadingPaymentDetails, setLoadingPaymentDetails] = useState(false);
  const [showAddDiscount, setShowAddDiscount] = useState(false);
  const [newDiscount, setNewDiscount] = useState({
    name: '',
    type: 'percentage',
    value: 0,
    yearMonth: ''
  });

  // Facility users — used to prefill the statement owner
  const [facilityUsers, setFacilityUsers] = useState([]);
  const [loadingFacilityUsers, setLoadingFacilityUsers] = useState(false);
  const [selectedStatementUserId, setSelectedStatementUserId] = useState('');

  useEffect(() => {
    if (facilityId) {
      fetchSettings();
      fetchGLAccounts();
      fetchBillerAddresses();
      fetchBankDetails();
      fetchFacilityPaymentDetails();
      fetchFacilityUsers();
    }
  }, [facilityId]);

  const fetchBankDetails = async () => {
    try {
      setLoadingBankDetails(true);
      if (!facilityId) return;
      const response = await makeRequest2(`${getBankDetailsURL}/${facilityId}`, "GET");
      if (response && response.success && response.data) {
        let details = [];
        if (Array.isArray(response.data)) {
          details = response.data;
        } else if (typeof response.data === 'object' && response.data !== null) {
          if (response.data.data && Array.isArray(response.data.data)) {
            details = response.data.data;
          } else if (response.data.bankDetails && Array.isArray(response.data.bankDetails)) {
            details = response.data.bankDetails;
          } else {
            details = [response.data];
          }
        }
        setBankDetails(details);
      } else {
        setBankDetails([]);
      }
    } catch (error) {
      toastify("Error fetching bank details.", "error");
      setBankDetails([]);
    } finally {
      setLoadingBankDetails(false);
    }
  };

  const fetchFacilityPaymentDetails = async () => {
  try {
    setLoadingPaymentDetails(true);
    if (!facilityId) return;
    const response = await makeRequest2(
      `${FETCH_FACILITY_PAYMENT_DETAILS_URL}/${facilityId}`,
      "GET"
    );
    if (response && response.success && response.data) {
      let details = [];
      if (Array.isArray(response.data)) {
        details = response.data;
      } else if (typeof response.data === 'object' && response.data !== null) {
        if (response.data.data && Array.isArray(response.data.data)) {
          details = response.data.data;
        } else {
          details = [response.data];
        }
      }
      setFacilityPaymentDetails(details);
    } else {
      setFacilityPaymentDetails([]);
    }
  } catch (error) {
    toastify("Error fetching facility payment details.", "error");
    setFacilityPaymentDetails([]);
  } finally {
    setLoadingPaymentDetails(false);
  }
};

  // Fetch the facility's users (same endpoint as the Users tab) so the statement owner can be picked from a dropdown
  const fetchFacilityUsers = async () => {
    try {
      setLoadingFacilityUsers(true);
      if (!facilityId) return;
      const response = await makeRequest2(`${getUsersURL}/${facilityId}`, "GET");
      if (response && response.success) {
        const usersList = Array.isArray(response.data) ? response.data : [];
        setFacilityUsers(usersList);
      } else {
        setFacilityUsers([]);
      }
    } catch (error) {
      toastify("Error fetching facility users.", "error");
      setFacilityUsers([]);
    } finally {
      setLoadingFacilityUsers(false);
    }
  };

  const handleBankPaymentSelection = (bankId, enabled) => {
    setFormData(prev => ({
      ...prev,
      paymentMethods: {
        ...prev.paymentMethods,
        bankPayment: {
          status: enabled,
          paymentId: enabled ? bankId : ''
        }
      }
    }));
  };

  const handleMobilePaymentSelection = (paymentId, enabled) => {
    setFormData(prev => ({
      ...prev,
      paymentMethods: {
        ...prev.paymentMethods,
        mobilePayment: {
          status: enabled,
          paymentId: enabled ? paymentId : ''
        }
      }
    }));
  };

  const fetchBillerAddresses = async () => {
    try {
      setLoadingBillerAddresses(true);
      if (!facilityId) return;
      const response = await makeRequest2(`${getBillerAddressesURL}/${facilityId}`, "GET");
      if (response && response.success && response.data) {
        let addresses = [];
        if (Array.isArray(response.data)) {
          addresses = response.data;
        } else if (typeof response.data === 'object' && response.data !== null) {
          if (response.data.data && Array.isArray(response.data.data)) {
            addresses = response.data.data;
          } else if (response.data.addresses && Array.isArray(response.data.addresses)) {
            addresses = response.data.addresses;
          } else {
            addresses = [response.data];
          }
        }
        setBillerAddresses(addresses);
      } else {
        setBillerAddresses([]);
      }
    } catch (error) {
      toastify("Error fetching biller addresses.", "error");
      setBillerAddresses([]);
    } finally {
      setLoadingBillerAddresses(false);
    }
  };

  const handleAddDiscount = () => {
    if (!newDiscount.name.trim()) {
      toastify('Please enter a discount name', 'error');
      return;
    }
    if (!newDiscount.yearMonth) {
      toastify('Please select a year-month', 'error');
      return;
    }
    if (newDiscount.value <= 0) {
      toastify('Please enter a valid discount value', 'error');
      return;
    }
    const existingDiscount = formData.discounts.find(d => d.yearMonth === newDiscount.yearMonth);
    if (existingDiscount) {
      toastify('A discount already exists for this month', 'error');
      return;
    }
    setFormData(prev => ({
      ...prev,
      discounts: [...prev.discounts, { ...newDiscount }]
    }));
    setNewDiscount({ name: '', type: 'percentage', value: 0, yearMonth: '' });
    setShowAddDiscount(false);
    toastify('Discount added successfully', 'success');
  };

  const handleRemoveDiscount = (index) => {
    setFormData(prev => ({
      ...prev,
      discounts: prev.discounts.filter((_, i) => i !== index)
    }));
    toastify('Discount removed successfully', 'success');
  };

  const generateYearMonthOptions = () => {
    const options = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    for (let year = currentYear; year <= currentYear + 2; year++) {
      const startMonth = (year === currentYear) ? currentMonth : 1;
      for (let month = startMonth; month <= 12; month++) {
        const value = `${year}-${month.toString().padStart(2, '0')}`;
        const label = new Date(year, month - 1).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
        options.push({ value, label });
      }
    }
    return options;
  };

  const handleBillerAddressSelection = (billerAddressId) => {
    setSelectedBillerAddressId(billerAddressId);
    if (billerAddressId) {
      const selectedAddress = billerAddresses.find(addr => addr._id === billerAddressId);
      if (selectedAddress) {
        setFormData(prev => ({
          ...prev,
          billerAddress: {
            name: selectedAddress.name || selectedAddress.companyName || '',
            email: selectedAddress.email || '',
            phone: selectedAddress.phone || '',
            address: selectedAddress.address || '',
            city: selectedAddress.city || ''
          }
        }));
        toastify("Biller address details filled successfully", "success");
      }
    } else {
      setFormData(prev => ({
        ...prev,
        billerAddress: { name: '', email: '', phone: '', address: '', city: '' }
      }));
    }
  };

  const handleBillerAddressFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      billerAddress: { ...prev.billerAddress, [field]: value }
    }));
    if (selectedBillerAddressId) setSelectedBillerAddressId('');
  };

  // Prefill statementOwner name/email/phone from a selected facility user
  const handleStatementUserSelection = (userId) => {
    setSelectedStatementUserId(userId);
    if (userId) {
      const selectedUser = facilityUsers.find(u => u._id === userId);
      if (selectedUser) {
        setFormData(prev => ({
          ...prev,
          notifications: {
            ...prev.notifications,
            statements: {
              ...prev.notifications.statements,
              statementOwner: {
                name: selectedUser.fullName || [selectedUser.firstName, selectedUser.lastName].filter(Boolean).join(' '),
                email: selectedUser.email || '',
                phone: selectedUser.phoneNumber || ''
              }
            }
          }
        }));
        toastify("Statement owner details filled successfully", "success");
      }
    } else {
      setFormData(prev => ({
        ...prev,
        notifications: {
          ...prev.notifications,
          statements: {
            ...prev.notifications.statements,
            statementOwner: { name: '', email: '', phone: '' }
          }
        }
      }));
    }
  };

  const handleStatementOwnerFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        statements: {
          ...prev.notifications.statements,
          statementOwner: { ...prev.notifications.statements.statementOwner, [field]: value }
        }
      }
    }));
    if (selectedStatementUserId) setSelectedStatementUserId('');
  };

  const fetchGLAccounts = async () => {
    try {
      setLoadingGLAccounts(true);
      if (!facilityId) return;
      const response = await makeRequest2(`${getGLAccountsURL}/${facilityId}`, "GET");
      if (response && response.success) {
        setGlAccounts(response.data?.data || []);
      } else {
        setGlAccounts([]);
      }
    } catch (error) {
      toastify("Error fetching GL accounts.", "error");
      setGlAccounts([]);
    } finally {
      setLoadingGLAccounts(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await makeRequest2(`${GET_WATER_METER_SETTINGS_URL}/${facilityId}`, "GET");
      if (response.success && response.data) {
        const settingsData = response.data.data || response.data;
        const fetchedStatements = settingsData.notifications?.statements || {};
        const formattedData = {
          ...settingsData,
          freeWaterAllowance: settingsData.freeWaterAllowance || 0,
          discounts: settingsData.discounts || [],
          billerAddress: settingsData.billerAddress || { name: '', email: '', phone: '', address: '', city: '' },
          glAccounts: settingsData.glAccounts || { invoice: { debit: '', credit: '' }, payment: { debit: '', credit: '' } },
          paymentMethods: settingsData.paymentMethods || { mobilePayment: { status: false, paymentId: '' }, bankPayment: { status: false, paymentId: '' } },
          notifications: settingsData.notifications || {
            usageAlerts: { enabled: false, daily: false, weekly: false, monthly: false },
            statements: { enabled: false, statementOwner: { name: '', email: '', phone: '' } },
            paymentReminders: { enabled: false, daysBeforeDue: 3, frequency: 'once' }
          }
        };
        // Ensure statementOwner always exists even if older saved settings predate this field
        formattedData.notifications = {
          ...formattedData.notifications,
          statements: {
            enabled: fetchedStatements.enabled || false,
            statementOwner: {
              name: fetchedStatements.statementOwner?.name || '',
              email: fetchedStatements.statementOwner?.email || '',
              phone: fetchedStatements.statementOwner?.phone || ''
            }
          }
        };
        setFormData(formattedData);
      } else {
        toastify(response.message || "Error fetching settings", "error");
      }
    } catch (error) {
      toastify("Error fetching settings", "error");
    }
  };

  const handleSave = async () => {
    try {
      // Validate required biller address fields
      if (!formData.billerAddress.name || !formData.billerAddress.email ||
        !formData.billerAddress.phone || !formData.billerAddress.address ||
        !formData.billerAddress.city) {
        toastify('Please fill in all required biller address fields (Name, Email, Phone, Address, City)', 'error');
        return;
      }

      // GL accounts are optional — no validation required

      let response;
      if (formData._id) {
        response = await makeRequest2(`${UPDATE_WATER_METER_SETTINGS_URL}/${facilityId}`, "PUT", formData);
      } else {
        response = await makeRequest2(`${ADD_WATER_METER_SETTINGS_URL}/${facilityId}`, "POST", formData);
      }

      if (response.success) {
        toastify("Settings saved successfully", "success");
        fetchSettings();
      } else {
        toastify(response.error || response.message || "Failed to save settings", "error");
      }
    } catch (error) {
      toastify("Error saving settings", "error");
    }
  };

  const handleGLAccountChange = (section, type, value) => {
    setFormData(prev => ({
      ...prev,
      glAccounts: {
        ...prev.glAccounts,
        [section]: { ...prev.glAccounts[section], [type]: value }
      }
    }));
  };

  const handleUsageAlertsChange = (enabled) => {
    setFormData(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        usageAlerts: {
          enabled,
          daily: enabled ? prev.notifications.usageAlerts.daily : false,
          weekly: enabled ? prev.notifications.usageAlerts.weekly : false,
          monthly: enabled ? prev.notifications.usageAlerts.monthly : false
        }
      }
    }));
  };

  const handleUsageAlertPeriodChange = (period, checked) => {
    setFormData(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        usageAlerts: { ...prev.notifications.usageAlerts, [period]: checked }
      }
    }));
  };

  const handleUsageAlertsSelectAll = (checked) => {
    setFormData(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        usageAlerts: { ...prev.notifications.usageAlerts, daily: checked, weekly: checked, monthly: checked }
      }
    }));
  };

  // Only flip the `enabled` flag — keep whatever statementOwner details are already filled in
  const handleStatementsChange = (enabled) => {
    setFormData(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        statements: { ...prev.notifications.statements, enabled }
      }
    }));
  };

  const handlePaymentRemindersChange = (enabled) => {
    setFormData(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        paymentReminders: { ...prev.notifications.paymentReminders, enabled }
      }
    }));
  };

  const handlePaymentReminderDaysChange = (days) => {
    setFormData(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        paymentReminders: { ...prev.notifications.paymentReminders, daysBeforeDue: days }
      }
    }));
  };

  const handlePaymentReminderFrequencyChange = (frequency) => {
    setFormData(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        paymentReminders: { ...prev.notifications.paymentReminders, frequency }
      }
    }));
  };

  return (
    <div className="card">
      <div className="card-header">
        <h4 className="card-title mb-0">Billing Settings</h4>
      </div>
      <div className="card-body">
        <div className="settings-container">

          {/* Biller Address Configuration Section */}
          <h5 className="section-header mb-3">Biller Address Configuration</h5>
          <div className="row mb-4">
            <div className="col-12 mb-3">
              {loadingBillerAddresses && <div className="alert alert-info">Loading biller addresses...</div>}
              {!loadingBillerAddresses && billerAddresses.length === 0 && (
                <div className="alert alert-warning">
                  No biller addresses found. Please create biller addresses first in the Biller Address tab.
                </div>
              )}
            </div>

            {billerAddresses.length > 0 && (
              <div className="col-12 mb-4">
                <div className="form-group">
                  <label className="form-label fw-bold">Select Biller Address</label>
                  <select
                    className="form-control w-100"
                    value={selectedBillerAddressId}
                    onChange={(e) => handleBillerAddressSelection(e.target.value)}
                  >
                    <option value="">-- Select a biller address to auto-fill details --</option>
                    {billerAddresses.map((address) => (
                      <option key={address._id} value={address._id}>
                        {address.name} - {address.companyName} ({address.city})
                        {address.isDefault ? ' (Default)' : ''}
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">Select from existing biller addresses or manually fill the fields below</small>
                </div>
              </div>
            )}

            <div className="col-12">
              <h6 className="text-primary mb-3">Biller Information for Invoices</h6>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label fw-bold">Biller Name*</label>
                    <input type="text" className="form-control" value={formData.billerAddress.name}
                      onChange={(e) => handleBillerAddressFieldChange('name', e.target.value)}
                      placeholder="Company or organization name" required />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label fw-bold">Email*</label>
                    <input type="email" className="form-control" value={formData.billerAddress.email}
                      onChange={(e) => handleBillerAddressFieldChange('email', e.target.value)}
                      placeholder="billing@company.com" required />
                  </div>
                </div>
                <div className="col-md-6 mt-3">
                  <div className="form-group">
                    <label className="form-label fw-bold">Phone*</label>
                    <input type="tel" className="form-control" value={formData.billerAddress.phone}
                      onChange={(e) => handleBillerAddressFieldChange('phone', e.target.value)}
                      placeholder="+254 XXX XXX XXX" required />
                  </div>
                </div>
                <div className="col-md-6 mt-3">
                  <div className="form-group">
                    <label className="form-label fw-bold">City*</label>
                    <input type="text" className="form-control" value={formData.billerAddress.city}
                      onChange={(e) => handleBillerAddressFieldChange('city', e.target.value)}
                      placeholder="Nairobi" required />
                  </div>
                </div>
                <div className="col-12 mt-3">
                  <div className="form-group">
                    <label className="form-label fw-bold">Address*</label>
                    <textarea className="form-control" rows="2" value={formData.billerAddress.address}
                      onChange={(e) => handleBillerAddressFieldChange('address', e.target.value)}
                      placeholder="Street address, building, floor, etc." required />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Methods Configuration Section */}
          <h5 className="section-header mb-3">Payment Methods Configuration</h5>
          <div className="row mb-4">
            <div className="col-12 mb-3">
              <h6 className="text-primary mb-3">Enable Payment Methods for Water Bills</h6>
              <p className="text-muted">Configure which payment methods are available for customers to pay their water bills.</p>
            </div>

            <div className="col-md-6">
              <div className="card border">
                <div className="card-header bg-light"><h6 className="mb-0">Bank Payment</h6></div>
                <div className="card-body">
                  <div className="form-check mb-3">
                    <input type="checkbox" className="form-check-input" id="enableBankPayment"
                      checked={formData.paymentMethods.bankPayment.status}
                      onChange={(e) => handleBankPaymentSelection(formData.paymentMethods.bankPayment.paymentId, e.target.checked)} />
                    <label className="form-check-label fw-bold" htmlFor="enableBankPayment">Enable Bank Payment</label>
                  </div>
                  {formData.paymentMethods.bankPayment.status && (
                    <div className="form-group">
                      <label className="form-label fw-bold">Select Bank Account</label>
                      {loadingBankDetails && <div className="alert alert-info py-2">Loading bank details...</div>}
                      {!loadingBankDetails && bankDetails.length === 0 && (
                        <div className="alert alert-warning py-2">No bank details found. Please add bank details first.</div>
                      )}
                      {bankDetails.length > 0 && (
                        <select className="form-control" value={formData.paymentMethods.bankPayment.paymentId}
                          onChange={(e) => handleBankPaymentSelection(e.target.value, true)}>
                          <option value="">-- Select Bank Account --</option>
                          {bankDetails.map((bank) => (
                            <option key={bank._id} value={bank._id}>
                              {bank.bankName} - {bank.accountName} ({bank.accountNumber}){bank.isDefault ? ' (Default)' : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card border">
                <div className="card-header bg-light"><h6 className="mb-0">Mobile Payment (M-Pesa)</h6></div>
                <div className="card-body">
                  <div className="form-check mb-3">
                    <input type="checkbox" className="form-check-input" id="enableMobilePayment"
                      checked={formData.paymentMethods.mobilePayment.status}
                      onChange={(e) => handleMobilePaymentSelection(formData.paymentMethods.mobilePayment.paymentId, e.target.checked)} />
                    <label className="form-check-label fw-bold" htmlFor="enableMobilePayment">Enable Mobile Payment</label>
                  </div>
                  {formData.paymentMethods.mobilePayment.status && (
                    <div className="form-group">
                      <label className="form-label fw-bold">Select Paybill Configuration</label>
                      {loadingPaymentDetails && <div className="alert alert-info py-2">Loading payment details...</div>}
                      {!loadingPaymentDetails && facilityPaymentDetails.length === 0 && (
                        <div className="alert alert-warning py-2">No payment details found. Please add M-Pesa paybill configuration first.</div>
                      )}
                      {facilityPaymentDetails.length > 0 && (
                        <select className="form-control" value={formData.paymentMethods.mobilePayment.paymentId}
                          onChange={(e) => handleMobilePaymentSelection(e.target.value, true)}>
                          <option value="">-- Select Paybill Configuration --</option>
                          {facilityPaymentDetails.map((payment) => (
                            <option key={payment._id} value={payment._id}>
                              Paybill: {payment.shortCode} - {payment.module}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <h5 className="section-header mb-3">Billing Amounts</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label fw-bold">Minimum Amount (KES)</label>
                <InputNumber className="w-100" value={formData.minAmount}
                  onChange={(e) => setFormData({ ...formData, minAmount: e.value })} />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label fw-bold">Maximum Amount (KES)</label>
                <InputNumber className="w-100" value={formData.maxAmount}
                  onChange={(e) => setFormData({ ...formData, maxAmount: e.value })} />
              </div>
            </div>
          </div>

          <h5 className="section-header mb-3">Allowed Meter Reading Thresholds</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label fw-bold">Low Threshold (m³)</label>
                <InputNumber className="w-100" value={formData.lowThreshold}
                  onChange={(e) => setFormData({ ...formData, lowThreshold: e.value })} min={0}
                  tooltip="Alert if meter reading falls below this threshold" tooltipOptions={{ position: 'top' }} />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label fw-bold">High Threshold (m³)</label>
                <InputNumber className="w-100" value={formData.highThreshold}
                  onChange={(e) => setFormData({ ...formData, highThreshold: e.value })} min={0}
                  tooltip="Alert if meter reading exceeds this threshold" tooltipOptions={{ position: 'top' }} />
              </div>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label fw-bold">Free Water Allowance (m³)</label>
                <InputNumber className="w-100" value={formData.freeWaterAllowance}
                  onChange={(e) => setFormData({ ...formData, freeWaterAllowance: e.value })} min={0}
                  tooltip="Users won't be billed for water consumption up to this amount" tooltipOptions={{ position: 'top' }} />
                <small className="text-muted">Set to 0 if no free water allowance</small>
              </div>
            </div>
          </div>

          <h5 className="section-header mb-3">Billing Schedule</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label fw-bold">Grace Period (Days)</label>
                <InputNumber className="w-100" value={formData.gracePeriod}
                  onChange={(e) => setFormData({ ...formData, gracePeriod: e.value })} />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label fw-bold">Invoice Day</label>
                <InputNumber className="w-100" value={formData.invoiceDay}
                  onChange={(e) => setFormData({ ...formData, invoiceDay: e.value })} min={0} max={30}
                  tooltip="The day of the month when invoices are generated" tooltipOptions={{ position: 'top' }} />
              </div>
            </div>
          </div>

          <h5 className="section-header mb-3">Payment Enforcement</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label fw-bold">Enforce Minimum Payment</label>
                <select className="form-control w-100" value={formData.enforcePayment}
                  onChange={(e) => setFormData({ ...formData, enforcePayment: e.target.value })}>
                  <option value="">Select an option</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
            {formData.enforcePayment === "yes" && (
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label fw-bold">Minimum Payment Amount (KES)</label>
                  <InputNumber className="w-100" value={formData.minimumPaymentAmount}
                    onChange={(e) => setFormData({ ...formData, minimumPaymentAmount: e.value })} />
                </div>
              </div>
            )}
          </div>

          <h5 className="section-header mb-3">Tariff Settings</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label fw-bold">Enable Tariff</label>
                <select className="form-control w-100" value={formData.tariff}
                  onChange={(e) => setFormData({ ...formData, tariff: e.target.value })}>
                  <option value="">Select Tariff</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
            {formData.tariff === "yes" && (
              <>
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label fw-bold">Tariff Amount (Per Unit m³) Analog Meter</label>
                    <InputNumber className="w-100" value={formData.tariffAmount}
                      onChange={(e) => setFormData({ ...formData, tariffAmount: e.value })} />
                  </div>
                </div>
                <div className="col-md-6 mt-3">
                  <div className="form-group">
                    <label className="form-label fw-bold">Tariff Amount (Per Unit m³) Smart Meters</label>
                    <InputNumber className="w-100" value={formData.tariffAmountSmart}
                      onChange={(e) => setFormData({ ...formData, tariffAmountSmart: e.value })} />
                  </div>
                </div>
                <div className="col-md-6 mt-3">
                  <div className="form-group">
                    <label className="form-label fw-bold">Fixed Amount (Per Meter) (Monthly)</label>
                    <InputNumber className="w-100" value={formData.fixedTariffAmount}
                      onChange={(e) => setFormData({ ...formData, fixedTariffAmount: e.value })} />
                  </div>
                </div>
                <div className="col-md-6 mt-3">
                  <div className="form-group">
                    <label className="form-label fw-bold">Meter Loan Amount (KES)</label>
                    <InputNumber className="w-100" value={formData.meterLoan}
                      onChange={(e) => setFormData({ ...formData, meterLoan: e.value })}
                      tooltip="Monthly amount charged for meter financing" tooltipOptions={{ position: 'top' }} />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* GL Accounts Section — Optional */}
          <h5 className="section-header mb-1">GL Account Configuration</h5>
          <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
            <i className="pi pi-info-circle me-1"></i>
            Optional — configure GL accounts for accounting entries if required.
          </p>
          <div className="row mb-4">
            <div className="col-12 mb-3">
              {loadingGLAccounts && <div className="alert alert-info">Loading GL accounts...</div>}
              {!loadingGLAccounts && glAccounts.length === 0 && (
                <div className="alert alert-secondary">
                  No GL accounts found. You can create GL accounts separately — this section is optional.
                </div>
              )}
            </div>

            {glAccounts.length > 0 && (
              <>
                <div className="col-12 mb-3">
                  <h6 className="text-primary">Water Invoice GL Entries <span className="badge bg-secondary ms-2" style={{ fontSize: '0.7rem', fontWeight: 'normal' }}>Optional</span></h6>
                  <p className="text-muted small">Configure GL accounts for water billing invoice entries</p>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label fw-bold">Invoice Debit Account</label>
                    <select className="form-control w-100" value={formData.glAccounts?.invoice?.debit || ''}
                      onChange={(e) => handleGLAccountChange('invoice', 'debit', e.target.value)}>
                      <option value="">-- Select Debit Account (Optional) --</option>
                      {glAccounts.map((account) => (
                        <option key={account._id} value={account._id}>
                          {account.accountCode} - {account.accountName} ({account.accountType})
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Account to be debited when water invoices are generated</small>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label fw-bold">Invoice Credit Account</label>
                    <select className="form-control w-100" value={formData.glAccounts?.invoice?.credit || ''}
                      onChange={(e) => handleGLAccountChange('invoice', 'credit', e.target.value)}>
                      <option value="">-- Select Credit Account (Optional) --</option>
                      {glAccounts.map((account) => (
                        <option key={account._id} value={account._id}>
                          {account.accountCode} - {account.accountName} ({account.accountType})
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Account to be credited when water invoices are generated</small>
                  </div>
                </div>

                <div className="col-12 mb-3 mt-4">
                  <h6 className="text-primary">Water Payment GL Entries <span className="badge bg-secondary ms-2" style={{ fontSize: '0.7rem', fontWeight: 'normal' }}>Optional</span></h6>
                  <p className="text-muted small">Configure GL accounts for water payment entries</p>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label fw-bold">Payment Debit Account</label>
                    <select className="form-control w-100" value={formData.glAccounts?.payment?.debit || ''}
                      onChange={(e) => handleGLAccountChange('payment', 'debit', e.target.value)}>
                      <option value="">-- Select Debit Account (Optional) --</option>
                      {glAccounts.map((account) => (
                        <option key={account._id} value={account._id}>
                          {account.accountCode} - {account.accountName} ({account.accountType})
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Account to be debited when water payments are received</small>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label fw-bold">Payment Credit Account</label>
                    <select className="form-control w-100" value={formData.glAccounts?.payment?.credit || ''}
                      onChange={(e) => handleGLAccountChange('payment', 'credit', e.target.value)}>
                      <option value="">-- Select Credit Account (Optional) --</option>
                      {glAccounts.map((account) => (
                        <option key={account._id} value={account._id}>
                          {account.accountCode} - {account.accountName} ({account.accountType})
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Account to be credited when water payments are received</small>
                  </div>
                </div>
              </>
            )}
          </div>

          <h5 className="section-header mb-3">Other Charges</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label fw-bold">Enable Other Charges</label>
                <select className="form-control w-100" value={formData.otherCharges}
                  onChange={(e) => setFormData({ ...formData, otherCharges: e.target.value })}>
                  <option value="">Select Option</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
            {formData.otherCharges === "yes" && (
              <>
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label fw-bold">Sewerage Charge (KES)</label>
                    <InputNumber className="w-100" value={formData.sewerageCharge}
                      onChange={(e) => setFormData({ ...formData, sewerageCharge: e.value })} />
                  </div>
                </div>
                <div className="col-md-6 mt-3">
                  <div className="form-group">
                    <label className="form-label fw-bold">Fixed Charge (KES)</label>
                    <InputNumber className="w-100" value={formData.fixedCharge}
                      onChange={(e) => setFormData({ ...formData, fixedCharge: e.value })} />
                  </div>
                </div>
                <div className="col-md-6 mt-3">
                  <div className="form-group">
                    <label className="form-label fw-bold">VAT (Percentage %)</label>
                    <InputNumber className="w-100" value={formData.vatPercentage}
                      onChange={(e) => setFormData({ ...formData, vatPercentage: e.value })}
                      min={0} max={100} suffix="%" />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Discounts Section */}
          <h5 className="section-header mb-3">Discount Settings</h5>
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border">
                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Monthly Discounts</h6>
                  <Button label="Add New Discount" className="btn btn-sm btn-primary"
                    onClick={() => setShowAddDiscount(!showAddDiscount)} />
                </div>
                <div className="card-body">
                  <p className="text-muted mb-3">
                    Configure monthly discounts for water bills. Each discount applies to a specific month and year.
                  </p>

                  {showAddDiscount && (
                    <div className="border rounded p-3 mb-3 bg-light">
                      <h6 className="text-primary mb-3">Add New Discount</h6>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label fw-bold">Discount Name</label>
                            <input type="text" className="form-control" value={newDiscount.name}
                              onChange={(e) => setNewDiscount({ ...newDiscount, name: e.target.value })}
                              placeholder="e.g., Holiday Discount, Summer Special" maxLength="100" />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label fw-bold">Year-Month*</label>
                            <select className="form-control" value={newDiscount.yearMonth}
                              onChange={(e) => setNewDiscount({ ...newDiscount, yearMonth: e.target.value })}>
                              <option value="">-- Select Month --</option>
                              {generateYearMonthOptions().map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label fw-bold">Discount Type*</label>
                            <select className="form-control" value={newDiscount.type}
                              onChange={(e) => setNewDiscount({ ...newDiscount, type: e.target.value })}>
                              <option value="percentage">Percentage (%)</option>
                              <option value="fixed_amount">Fixed Amount (KES)</option>
                            </select>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label fw-bold">
                              {newDiscount.type === 'percentage' ? 'Discount Percentage*' : 'Discount Amount (KES)*'}
                            </label>
                            <InputNumber className="w-100" value={newDiscount.value}
                              onChange={(e) => setNewDiscount({ ...newDiscount, value: e.value })}
                              min={0} max={newDiscount.type === 'percentage' ? 100 : undefined}
                              suffix={newDiscount.type === 'percentage' ? '%' : ' KES'} />
                          </div>
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <Button label="Add Discount" className="btn btn-success btn-sm" onClick={handleAddDiscount} />
                        <Button label="Cancel" className="btn btn-secondary btn-sm" onClick={() => {
                          setShowAddDiscount(false);
                          setNewDiscount({ name: '', type: 'percentage', value: 0, yearMonth: '' });
                        }} />
                      </div>
                    </div>
                  )}

                  {formData.discounts.length === 0 ? (
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle me-2"></i>
                      No discounts configured. Click "Add New Discount" to create your first discount.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Discount Name</th>
                            <th>Month</th>
                            <th>Type</th>
                            <th>Value</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.discounts.map((discount, index) => {
                            const [year, month] = discount.yearMonth.split('-');
                            const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
                            return (
                              <tr key={index}>
                                <td className="fw-bold">{discount.name}</td>
                                <td>{monthName}</td>
                                <td>
                                  <span className={`badge ${discount.type === 'percentage' ? 'bg-primary' : 'bg-success'}`}>
                                    {discount.type === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                                  </span>
                                </td>
                                <td className="fw-bold">
                                  {discount.type === 'percentage' ? `${discount.value}%` : `KES ${discount.value.toLocaleString()}`}
                                </td>
                                <td>
                                  <Button label="Remove" className="btn btn-sm btn-outline-danger"
                                    onClick={() => { if (window.confirm('Are you sure you want to remove this discount?')) handleRemoveDiscount(index); }}
                                    icon="pi pi-trash" />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Notifications Section */}
          <h5 className="section-header mb-3">Notification Settings</h5>
          <div className="row mb-4">
            <div className="col-lg-4">
              <div className="card border">
                <div className="card-header bg-light"><h6 className="mb-0">Usage Alerts</h6></div>
                <div className="card-body">
                  <div className="form-group mb-3">
                    <div className="d-flex align-items-center">
                      <Checkbox inputId="usageAlertsEnabled" checked={formData.notifications.usageAlerts.enabled}
                        onChange={(e) => handleUsageAlertsChange(e.checked)} />
                      <label htmlFor="usageAlertsEnabled" className="ms-2 mb-0 fw-bold">Enable Usage Alerts</label>
                    </div>
                    <small className="text-muted d-block mt-1">Send alerts for water usage patterns</small>
                  </div>
                  {formData.notifications.usageAlerts.enabled && (
                    <div className="notification-periods">
                      <h6 className="mb-3 text-primary">Alert Frequency</h6>
                      <div className="d-flex flex-column gap-3">
                        <div className="d-flex align-items-center">
                          <Checkbox inputId="selectAllUsage"
                            checked={formData.notifications.usageAlerts.daily && formData.notifications.usageAlerts.weekly && formData.notifications.usageAlerts.monthly}
                            onChange={(e) => handleUsageAlertsSelectAll(e.checked)} />
                          <label htmlFor="selectAllUsage" className="ms-2 mb-0 fw-bold">Select All Periods</label>
                        </div>
                        {['daily', 'weekly', 'monthly'].map(period => (
                          <div className="d-flex align-items-center" key={period}>
                            <Checkbox inputId={`${period}UsageAlerts`} checked={formData.notifications.usageAlerts[period]}
                              onChange={(e) => handleUsageAlertPeriodChange(period, e.checked)} />
                            <label htmlFor={`${period}UsageAlerts`} className="ms-2 mb-0">
                              {period.charAt(0).toUpperCase() + period.slice(1)} Usage Alerts
                            </label>
                          </div>
                        ))}
                      </div>
                      {(!formData.notifications.usageAlerts.daily && !formData.notifications.usageAlerts.weekly && !formData.notifications.usageAlerts.monthly) && (
                        <div className="alert alert-warning mt-3 py-2">
                          <small>Please select at least one alert frequency to receive usage alerts.</small>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="card border">
                <div className="card-header bg-light"><h6 className="mb-0">Payment Reminders</h6></div>
                <div className="card-body">
                  <div className="form-group mb-3">
                    <div className="d-flex align-items-center">
                      <Checkbox inputId="paymentRemindersEnabled" checked={formData.notifications.paymentReminders.enabled}
                        onChange={(e) => handlePaymentRemindersChange(e.checked)} />
                      <label htmlFor="paymentRemindersEnabled" className="ms-2 mb-0 fw-bold">Enable Payment Reminders</label>
                    </div>
                    <small className="text-muted d-block mt-1">Send reminders before payment due dates</small>
                  </div>
                  {formData.notifications.paymentReminders.enabled && (
                    <div className="reminder-settings">
                      <div className="form-group mb-3">
                        <label className="form-label fw-bold">Days Before Due Date</label>
                        <InputNumber className="w-100" value={formData.notifications.paymentReminders.daysBeforeDue}
                          onChange={(e) => handlePaymentReminderDaysChange(e.value)} min={1} max={30}
                          tooltip="Number of days before due date to send reminders" tooltipOptions={{ position: 'top' }} />
                        <small className="text-muted">Send reminders {formData.notifications.paymentReminders.daysBeforeDue} day(s) before due date</small>
                      </div>
                      <div className="form-group">
                        <label className="form-label fw-bold">Reminder Frequency</label>
                        <select className="form-control w-100" value={formData.notifications.paymentReminders.frequency}
                          onChange={(e) => handlePaymentReminderFrequencyChange(e.target.value)}>
                          <option value="once">Once Only</option>
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                        </select>
                        <small className="text-muted">How often to send reminders during the reminder period</small>
                      </div>
                      <div className="alert alert-info mt-3 py-2">
                        <small><i className="fas fa-info-circle me-1"></i>Reminders will be sent to customers with outstanding bills</small>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="card border">
                <div className="card-header bg-light"><h6 className="mb-0">Monthly Statements</h6></div>
                <div className="card-body">
                  <div className="form-group">
                    <div className="d-flex align-items-center">
                      <Checkbox inputId="statementsEnabled" checked={formData.notifications.statements.enabled}
                        onChange={(e) => handleStatementsChange(e.checked)} />
                      <label htmlFor="statementsEnabled" className="ms-2 mb-0 fw-bold">Enable Monthly Statements</label>
                    </div>
                    <small className="text-muted d-block mt-1">Send monthly billing statements to customers</small>
                  </div>
                  {formData.notifications.statements.enabled && (
                    <div className="statement-owner-settings mt-3">
                      <h6 className="mb-2 text-primary">Statement Owner</h6>
                      <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
                        Who should receive a copy of the monthly statements.
                      </p>

                      <div className="form-group mb-3">
                        <label className="form-label fw-bold">Select from Users</label>
                        {loadingFacilityUsers && (
                          <div className="alert alert-info py-2">Loading users...</div>
                        )}
                        {!loadingFacilityUsers && facilityUsers.length === 0 && (
                          <div className="alert alert-warning py-2">
                            No users found. You can still enter details manually below.
                          </div>
                        )}
                        {facilityUsers.length > 0 && (
                          <select
                            className="form-control"
                            value={selectedStatementUserId}
                            onChange={(e) => handleStatementUserSelection(e.target.value)}
                          >
                            <option value="">-- Select a user to auto-fill details --</option>
                            {facilityUsers.map((user) => (
                              <option key={user._id} value={user._id}>
                                {user.fullName || [user.firstName, user.lastName].filter(Boolean).join(' ')} ({user.email})
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div className="form-group mb-2">
                        <label className="form-label fw-bold">Name</label>
                        <input type="text" className="form-control"
                          value={formData.notifications.statements.statementOwner.name}
                          onChange={(e) => handleStatementOwnerFieldChange('name', e.target.value)}
                          placeholder="Full name" />
                      </div>
                      <div className="form-group mb-2">
                        <label className="form-label fw-bold">Email</label>
                        <input type="email" className="form-control"
                          value={formData.notifications.statements.statementOwner.email}
                          onChange={(e) => handleStatementOwnerFieldChange('email', e.target.value)}
                          placeholder="name@example.com" />
                      </div>
                      <div className="form-group mb-2">
                        <label className="form-label fw-bold">Phone</label>
                        <input type="tel" className="form-control"
                          value={formData.notifications.statements.statementOwner.phone}
                          onChange={(e) => handleStatementOwnerFieldChange('phone', e.target.value)}
                          placeholder="+254 XXX XXX XXX" />
                      </div>

                      <div className="alert alert-info mt-2 py-2">
                        <small><i className="fas fa-info-circle me-1"></i>Monthly statements will be automatically sent to all customers, and a copy will be sent to the statement owner above.</small>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-4">
            <div className="col-12">
              <Button label="Save Settings" className="btn btn-primary w-100" onClick={handleSave}
                disabled={loadingBillerAddresses || loadingBankDetails || loadingPaymentDetails} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;